import { useEffect, useState } from "react";
import { Login } from "../utils/usersAPI";
import {
  Link,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  Box,
  Button,
  TextField,
  Grid,
  Typography,
  Container,
  IconButton,
  FormControlLabel,
  Checkbox,
  InputAdornment,
} from "@mui/material";
import { PersonAdd } from "@mui/icons-material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

import {
  AddProductToCart,
  GetProductsInCartByUserId,
  UpdateProduct,
} from "../utils/productInCart";

import { setLoggedUser } from "../features/userSlice";
import { SET_LOGGED_PRODUCTINCART } from "../features/productInCartSlice";
import {
  syncPendingCartChanges,
} from "../helpers/syncPendingCartChanges";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/*
  עיצוב משותף לשדות:

  - פוקוס רגיל: מסגרת ורודה.
  - מעבר עכבר: מסגרת אפורה כהה.
  - שגיאה אמיתית בלבד: מסגרת אדומה של MUI.
*/
const accessibleTextFieldStyle = {
  direction: "rtl",

  "& .MuiInputLabel-root": {
    right: 30,
    left: "auto",
    transformOrigin: "top right",
  },

  "& legend": {
    textAlign: "right",
  },

  "& input": {
    textAlign: "right",
  },

  "& .MuiOutlinedInput-root": {
    backgroundColor: "#fff",

    "& fieldset": {
      borderColor: "#9e9e9e",
    },

    "&:hover fieldset": {
      borderColor: "#555",
    },

    /*
      מסגרת פוקוס ורודה ועדינה במקום אדומה.
    */
    "&.Mui-focused fieldset": {
      borderColor: "#D81B60",
      borderWidth: "2px",
    },

    /*
      כאשר השדה באמת במצב שגיאה,
      MUI יציג מסגרת אדומה.
    */
    "&.Mui-error fieldset": {
      borderColor: "#d32f2f",
      borderWidth: "2px",
    },
  },

  /*
    מבטל outline חיצוני שעלול להגיע מקובץ CSS גלובלי.
    המסגרת הוורודה שלמעלה נשארת כסימון פוקוס נגיש.
  */
  "& input:focus": {
    outline: "none",
  },
};

const SignIn = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);

  const [error, setError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] =
    useState("");

  const [showPassword, setShowPassword] =
    useState(false);

  const [isSubmitting, setIsSubmitting] =
    useState(false);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();

  /*
    מאפסים את שדה הסיסמה לאחר שגיאת התחברות.
    שגיאות של שדה ריק לא מחייבות למחוק את מה שהוקלד.
  */
  useEffect(() => {
    if (
      error &&
      !emailError &&
      !passwordError
    ) {
      setPassword("");
    }
  }, [error, emailError, passwordError]);

  const validateForm = () => {
    let isValid = true;

    setError("");
    setEmailError("");
    setPasswordError("");

    const trimmedEmail = email.trim();
    const trimmedPassword = password.trim();

    if (!trimmedEmail) {
      setEmailError("חובה להזין כתובת מייל");
      isValid = false;
    } else if (!EMAIL_REGEX.test(trimmedEmail)) {
      setEmailError("כתובת האימייל אינה חוקית");
      isValid = false;
    }

    if (!trimmedPassword) {
      setPasswordError("חובה להזין סיסמה");
      isValid = false;
    }

    return isValid;
  };

  const handleClickLogin = async (event) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    const user = {
      Email: email.trim(),
      Password: password.trim(),
    };

    setIsSubmitting(true);
    setError("");

    try {
      const loginData = await Login(user);

      const loggedUserFromServer = loginData.user;
      const token = loginData.token;
      const expirationTime =
        loginData.expirationTime;

      if (!loggedUserFromServer || !token) {
        setError("התקבלה תשובה לא תקינה מהשרת");
        return;
      }
      localStorage.setItem("token", token);

      if (expirationTime) {
        localStorage.setItem(
          "tokenExpirationTime",
          expirationTime
        );
      }

      dispatch(
        setLoggedUser(loggedUserFromServer)
      );

      /*
        כרגע המשתמש נשמר תמיד ב-localStorage,
        כמו שהיה בקוד המקורי שלך.

        אם תרצי ש"זכור אותי" ישפיע באמת,
        ניתן לשמור ב-localStorage רק כשהוא מסומן,
        וב-sessionStorage כשהוא לא מסומן.
      */
      localStorage.setItem(
        "loggedUser",
        JSON.stringify(loggedUserFromServer)
      );

      localStorage.setItem(
        "isAdmin",
        loggedUserFromServer.tipeUser ===
          "admin"
          ? "true"
          : "false"
      );

      if (
        loggedUserFromServer.tipeUser ===
        "admin"
      ) {
        navigate("/admin");
        return;
      }
      const syncedCartResponse =
        await syncPendingCartChanges(
          loggedUserFromServer.id
        );

      const updatedServerCart =
        syncedCartResponse?.data ??
        syncedCartResponse ??
        [];
      // const localCart =
      //   JSON.parse(
      //     localStorage.getItem("cart")
      //   ) || [];

      // const serverCart =
      //   await GetProductsInCartByUserId(
      //     loggedUserFromServer.id
      //   );

      // for (const item of localCart) {
      //   const existingItem = serverCart.find(
      //     (serverItem) =>
      //       serverItem.productId ===
      //       item.productId &&
      //       (serverItem.productOptionId ??
      //         null) ===
      //       (item.productOptionId ??
      //         null)
      //   );

      //   if (existingItem) {
      //     const newAmount =
      //       Number(
      //         existingItem.amount || 0
      //       ) +
      //       Number(item.amount || 0);

      //     await UpdateProduct(
      //       existingItem.id,
      //       {
      //         ...existingItem,
      //         amount: newAmount,
      //       }
      //     );
      //   } else {
      //     await AddProductToCart({
      //       amount: item.amount,
      //       customerId:
      //         loggedUserFromServer.id,
      //       productId: item.productId,
      //       productOptionId:
      //         item.productOptionId ??
      //         null,
      //     });
      //   }
      // }

      // const updatedServerCart =
      //   await GetProductsInCartByUserId(
      //     loggedUserFromServer.id
      //   );

      dispatch(
        SET_LOGGED_PRODUCTINCART(
          updatedServerCart
        )
      );

      localStorage.setItem(
        "cart",
        JSON.stringify(updatedServerCart)
      );

      if (updatedServerCart.length > 0) {
        navigate("/cart", {
          replace: true,
          state: {
            from: "/signIn",
          },
        });

        return;
      }

      if (location.state?.from) {
        navigate(
          location.state.from,
          {
            replace: true,
          }
        );

        return;
      }

      navigate("/", {
        replace: true,
      });
    } catch (err) {
      console.error(
        "login error:",
        err.response?.data || err
      );

      const serverError =
        typeof err.response?.data ===
          "string"
          ? err.response.data
          : err.response?.data?.message;

      setError(
        serverError ||
        "אירעה שגיאה בהתחברות. נסי שוב."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChangeEmail = (event) => {
    setEmail(event.target.value);
    setError("");
    setEmailError("");
  };

  const handleChangePassword = (event) => {
    setPassword(event.target.value);
    setError("");
    setPasswordError("");
  };

  return (
    <Container
      component="main"
      maxWidth="xs"
      dir="rtl"
      sx={{
        mt: {
          xs: 8,
          sm: 17.5,
        },
        mb: 6,
      }}
    >
      <Box
        component="form"
        noValidate
        onSubmit={handleClickLogin}
        aria-labelledby="signin-title"
      >
        <Typography
          id="signin-title"
          component="h1"
          variant="h4"
          align="center"
          gutterBottom
          sx={{
            fontWeight: 700,
            mb: 3,
          }}
        >
          התחברות משתמש קיים
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              id="signin-email"
              label="כתובת מייל"
              name="email"
              type="email"
              fullWidth
              required
              autoComplete="email"
              value={email}
              onChange={handleChangeEmail}
              error={Boolean(emailError)}
              helperText={emailError}
              inputProps={{
                "aria-describedby":
                  emailError
                    ? "signin-email-helper-text"
                    : undefined,
              }}
              sx={{
                ...accessibleTextFieldStyle,

                "& input": {
                  textAlign: "right",
                  paddingRight: "14px",
                },
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              id="signin-password"
              label="סיסמה"
              name="password"
              type={
                showPassword
                  ? "text"
                  : "password"
              }
              fullWidth
              required
              autoComplete="current-password"
              value={password}
              onChange={handleChangePassword}
              error={Boolean(passwordError)}
              helperText={passwordError}
              inputProps={{
                "aria-describedby":
                  passwordError
                    ? "signin-password-helper-text"
                    : undefined,
              }}
              sx={{
                ...accessibleTextFieldStyle,

                "& input": {
                  textAlign: "right",
                  paddingRight: "14px",
                  paddingLeft: "45px",
                },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      type="button"
                      edge="end"
                      onClick={() =>
                        setShowPassword(
                          (previousValue) =>
                            !previousValue
                        )
                      }
                      aria-label={
                        showPassword
                          ? "הסתרת הסיסמה"
                          : "הצגת הסיסמה"
                      }
                      aria-pressed={
                        showPassword
                      }
                      sx={{
                        color:
                          "text.primary",

                        "&:hover": {
                          backgroundColor:
                            "rgba(0, 0, 0, 0.06)",
                        },

                        "&:focus-visible": {
                          outline:
                            "3px solid #111",
                          outlineOffset:
                            "2px",
                        },
                      }}
                    >
                      {showPassword ? (
                        <VisibilityOff
                          aria-hidden="true"
                        />
                      ) : (
                        <Visibility
                          aria-hidden="true"
                        />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={rememberMe}
                  onChange={(event) =>
                    setRememberMe(
                      event.target.checked
                    )
                  }
                  name="rememberMe"
                  sx={{
                    color: "#444",

                    "&.Mui-checked": {
                      color: "#D81B60",
                    },

                    "&:focus-visible": {
                      outline:
                        "3px solid #111",
                      outlineOffset: "2px",
                    },
                  }}
                />
              }
              label="זכור אותי"
              sx={{
                mt: 0.5,
                mr: 0,
              }}
            />
          </Grid>
        </Grid>

        {error && (
          <Typography
            id="signin-general-error"
            role="alert"
            aria-live="assertive"
            color="error"
            sx={{
              mt: 2,
              mb: 1,
              textAlign: "right",
            }}
          >
            {error}
          </Typography>
        )}

        <Button
          type="submit"
          variant="contained"
          fullWidth
          disabled={isSubmitting}
          aria-describedby={
            error
              ? "signin-general-error"
              : undefined
          }
          sx={{
            mt: 2,
            minHeight: 44,
            fontWeight: 700,
            backgroundColor: "#D81B60",

            "&:hover": {
              backgroundColor: "#AD1457",
            },

            "&:focus-visible": {
              outline: "3px solid #111",
              outlineOffset: "3px",
            },
          }}
        >
          {isSubmitting
            ? "מתחברת..."
            : "התחברות"}
        </Button>

        <Typography
          component="p"
          variant="body2"
          align="center"
          sx={{ mt: 2 }}
        >
          <Link
            to="/forgot-password"
            style={{
              color: "#8E0E46",
              textDecoration: "underline",
              textUnderlineOffset: "3px",
            }}
          >
            שכחת סיסמה?
          </Link>
        </Typography>

        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            mt: 4,
          }}
        >
          <Button
            type="button"
            component={Link}
            to="/signUp"
            endIcon={
              <PersonAdd
                aria-hidden="true"
                sx={{
                  fontSize: "40px !important",
                  marginInlineStart: 1,
                }}
              />
            }
            sx={{
              minHeight: 44,
              color: "#0b071d",
              fontSize: "1.1rem",

              "&:hover": {
                color: "#fff",
                backgroundColor: "#D81B60",
              },

              "&:focus-visible": {
                outline: "3px solid #111",
                outlineOffset: "3px",
              },
            }}
          >
            הרשמה למשתמש חדש
          </Button>
        </Box>
      </Box>
    </Container>
  );
};

export default SignIn;